import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx } from "react/jsx-runtime";
import React from 'react';
import { AiFilterChip } from '~/components/AiSearchChips/AiFilterChip/AiFilterChip';
import { checkboxesToSet, nameSelectionHandler, setToCheckboxes } from '~/components/AiSearchChips/AiFilterChip/chipHandlers';
import { dateRangeChipUnit } from '~/components/AiSearchChips/DateRangeChip/dateRangeChipUnit';
import { FilterChip } from '~/components/AiSearchChips/FilterChip/FilterChip';
import { editableValue, isEmptyPredicate, isExcludeMode, isRangeModeOnly, multiValueGroup, parseNumbers, predicateFromNumbers, replaceMultiValue, valueLabelFor } from '~/components/AiSearchChips/intRangeChip.utils';
import { modifiedByChipUnit } from '~/components/AiSearchChips/modifiedByChipUnit';
import { OrValueGroupChip } from '~/components/AiSearchChips/OrValueGroupChip/OrValueGroupChip';
import { presentQuantifiers } from '~/components/AiSearchChips/ServicesChips/service-predicate';
import { ServicesChips } from '~/components/AiSearchChips/ServicesChips/ServicesChips';
import { SettingsChips } from '~/components/AiSearchChips/SettingsChips/SettingsChips';
import { renderedSettingsFields } from '~/components/AiSearchChips/SettingsChips/settingsGrammar';
import { currentValue } from '~/components/AiSearchChips/StringMatchChip/StringMatchChip.utils';
import { ALLOWED_DRAFTS_AI_SEARCH_STATUS } from '~/types/api/drafts-search';
import { draftsOrValueGroupFields, draftsOrValueGroupTexts } from '../draftsOrValueGroupFields';
import { selectedMode, togglePredicate } from '../EffectiveDateModeChip/EffectiveDateModeChip.utils';
import { localizeStatus } from '../StatusChip/StatusChip.utils';
const dateRangeChipTexts = (texts)=>_object_spread_props(_object_spread({}, texts.dateRange), {
        apply: texts.apply,
        all: texts.all
    });
const joinedLabel = (items, selected)=>selected.length === 0 ? undefined : selected.map((value)=>{
        var _ref;
        var _items_find;
        return (_ref = (_items_find = items.find((item)=>item.value === value)) === null || _items_find === void 0 ? void 0 : _items_find.label) !== null && _ref !== void 0 ? _ref : value;
    }).join(', ');
export const buildDraftsPredicateChipTexts = (t, commonFilter, agr)=>({
        labels: {
            agreementName: t.agreementNameLabel(),
            clientName: t.clientNameLabel(),
            contactName: t.recipientNameLabel(),
            status: t.statusLabel(),
            effectiveDate: t.effectiveDateLabel(),
            lastUpdated: t.lastUpdatedLabel()
        },
        statusOptions: ALLOWED_DRAFTS_AI_SEARCH_STATUS.map((status)=>({
                value: status,
                label: localizeStatus(status, t)
            })),
        dateRange: {
            allTime: agr.filters.dateRange.allTime(),
            specificRange: agr.filters.dateRange.specificRange(),
            from: agr.filters.dateRange.from(),
            to: agr.filters.dateRange.to()
        },
        clientPresence: {
            label: t.clientPresenceFieldLabel(),
            any: t.clientPresenceAny(),
            none: t.clientPresenceNone()
        },
        reviewers: {
            label: t.draftFieldLabel(),
            any: t.reviewersAny(),
            none: t.reviewersNone()
        },
        effectiveDateMode: {
            label: t.effectiveDateModeLabel(),
            onAcceptance: t.effectiveDateModeOnAcceptance(),
            specificDate: t.effectiveDateModeSpecificDate()
        },
        modifiedBy: {
            label: t.modifiedByFieldLabel(),
            me: t.modifiedByValueMe()
        },
        apply: t.applyButton(),
        all: t.chipValueAll(),
        selectAll: commonFilter.selectAll(),
        netDays: {
            label: t.netDaysLabel(),
            suffix: t.netDaysSuffix(),
            validationError: t.netDaysValidationError(),
            valueIs: (value)=>t.netDaysValueIs({
                    value
                }),
            valueOver: (value)=>t.netDaysValueOver({
                    value
                }),
            valueUnder: (value)=>t.netDaysValueUnder({
                    value
                })
        },
        orValueGroup: {
            fields: draftsOrValueGroupFields(t),
            texts: draftsOrValueGroupTexts(t)
        }
    });
export const buildPredicateChipUnits = (param)=>{
    let { predicate, testIds, callbacks, clientNameOptions, contactNameOptions, agreementNameOptions, texts, settingsTone, serviceGrammar, settingsGrammar } = param;
    var _predicate_contactId, _predicate_paymentSettings;
    const nameRadioTexts = {
        apply: texts.apply,
        all: texts.all
    };
    const showServices = predicate.services != null && presentQuantifiers(predicate.services).length > 0;
    const units = [];
    if (predicate.agreementName != null) {
        const agreementName = predicate.agreementName;
        const current = currentValue(agreementName);
        units.push({
            key: 'agreementName',
            node: /*#__PURE__*/ _jsx(AiFilterChip, {
                mode: "radio",
                testId: testIds.AgreementName,
                fieldLabel: texts.labels.agreementName,
                items: agreementNameOptions,
                selected: current !== '' ? current : null,
                onApply: nameSelectionHandler({
                    current,
                    predicate: agreementName,
                    onChange: callbacks.onAgreementNameChange
                }),
                onRemove: ()=>callbacks.onAgreementNameChange(undefined),
                biPrefix: "drafts_ai_search_chip_agreement_name",
                texts: nameRadioTexts
            })
        });
    }
    if (predicate.clientName != null) {
        const clientName = predicate.clientName;
        const current = currentValue(clientName);
        units.push({
            key: 'clientName',
            node: /*#__PURE__*/ _jsx(AiFilterChip, {
                mode: "radio",
                testId: testIds.ClientName,
                fieldLabel: texts.labels.clientName,
                items: clientNameOptions,
                selected: current !== '' ? current : null,
                onApply: nameSelectionHandler({
                    current,
                    predicate: clientName,
                    onChange: callbacks.onClientNameChange
                }),
                onRemove: ()=>callbacks.onClientNameChange(undefined),
                biPrefix: "drafts_ai_search_chip_client_name",
                texts: nameRadioTexts
            })
        });
    }
    if (((_predicate_contactId = predicate.contactId) === null || _predicate_contactId === void 0 ? void 0 : _predicate_contactId.exists) != null) {
        const contactId = predicate.contactId;
        units.push({
            key: 'contactId',
            node: /*#__PURE__*/ _jsx(AiFilterChip, {
                mode: "toggle",
                testId: testIds.ClientPresence,
                fieldLabel: texts.clientPresence.label,
                valueLabel: contactId.exists ? texts.clientPresence.any : texts.clientPresence.none,
                onToggle: ()=>callbacks.onContactIdChange(_object_spread_props(_object_spread({}, contactId), {
                        exists: contactId.exists !== true
                    })),
                onRemove: ()=>callbacks.onContactIdChange(undefined),
                biPrefix: "drafts_ai_search_chip_client_presence"
            })
        });
    }
    if (predicate.reviewers != null) {
        const reviewers = predicate.reviewers;
        units.push({
            key: 'reviewers',
            node: /*#__PURE__*/ _jsx(FilterChip, {
                testId: testIds.Reviewers,
                fieldLabel: texts.reviewers.label,
                valueLabel: reviewers.exists ? texts.reviewers.any : texts.reviewers.none,
                onRemove: ()=>callbacks.onReviewersChange(undefined),
                removeBiName: "drafts_ai_search_chip_reviewers_remove"
            })
        });
    }
    if (predicate.contactName != null) {
        const contactName = predicate.contactName;
        const current = currentValue(contactName);
        units.push({
            key: 'contactName',
            node: /*#__PURE__*/ _jsx(AiFilterChip, {
                mode: "radio",
                testId: testIds.ContactName,
                fieldLabel: texts.labels.contactName,
                items: contactNameOptions,
                selected: current !== '' ? current : null,
                onApply: nameSelectionHandler({
                    current,
                    predicate: contactName,
                    onChange: callbacks.onContactNameChange
                }),
                onRemove: ()=>callbacks.onContactNameChange(undefined),
                biPrefix: "drafts_ai_search_chip_contact_name",
                texts: nameRadioTexts
            })
        });
    }
    if (predicate.status != null) {
        const { selected, operator } = setToCheckboxes(predicate.status);
        const shownStatuses = operator === 'exclude' ? texts.statusOptions.map((option)=>option.value).filter((value)=>!selected.includes(value)) : selected;
        units.push({
            key: 'status',
            node: /*#__PURE__*/ _jsx(AiFilterChip, {
                mode: "checkboxes",
                testId: testIds.Status,
                fieldLabel: texts.labels.status,
                items: texts.statusOptions,
                selected: selected,
                operator: operator,
                valueLabel: joinedLabel(texts.statusOptions, shownStatuses),
                onApply: (next)=>callbacks.onStatusChange(next.selected.length === 0 ? undefined : checkboxesToSet(next)),
                onRemove: ()=>callbacks.onStatusChange(undefined),
                biPrefix: "drafts_ai_search_chip_status"
            })
        });
    }
    if (predicate.effectiveDate != null) {
        units.push(dateRangeChipUnit({
            field: 'effectiveDate',
            predicate: predicate.effectiveDate,
            testId: testIds.EffectiveDate,
            label: texts.labels.effectiveDate,
            texts: dateRangeChipTexts(texts),
            onChange: callbacks.onEffectiveDateChange,
            biPrefix: 'drafts_ai_search_chip_effective_date',
            tone: settingsTone
        }));
    }
    if (predicate.effectiveDateMode != null) {
        const effectiveDateMode = predicate.effectiveDateMode;
        units.push({
            key: 'effectiveDateMode',
            node: /*#__PURE__*/ _jsx(AiFilterChip, {
                mode: "toggle",
                testId: testIds.EffectiveDateMode,
                fieldLabel: texts.effectiveDateMode.label,
                valueLabel: selectedMode(effectiveDateMode) === 'specificDate' ? texts.effectiveDateMode.specificDate : texts.effectiveDateMode.onAcceptance,
                onToggle: ()=>callbacks.onEffectiveDateModeChange(togglePredicate(effectiveDateMode)),
                onRemove: ()=>callbacks.onEffectiveDateModeChange(undefined),
                biPrefix: "drafts_ai_search_chip_effective_date_mode"
            })
        });
    }
    if (predicate.lastUpdatedAt != null) {
        units.push(dateRangeChipUnit({
            field: 'lastUpdatedAt',
            predicate: predicate.lastUpdatedAt,
            testId: testIds.LastUpdated,
            label: texts.labels.lastUpdated,
            texts: dateRangeChipTexts(texts),
            onChange: callbacks.onLastUpdatedAtChange,
            biPrefix: 'drafts_ai_search_chip_last_updated'
        }));
    }
    if (predicate.lastModifiedBy != null) {
        units.push(modifiedByChipUnit({
            predicate: predicate.lastModifiedBy,
            testId: testIds.ModifiedBy,
            texts: {
                label: texts.modifiedBy.label,
                me: texts.modifiedBy.me,
                apply: texts.apply
            },
            onChange: callbacks.onLastModifiedByChange,
            biPrefix: 'drafts_ai_search_chip_modified_by',
            lockSentinelActors: false
        }));
    }
    if (((_predicate_paymentSettings = predicate.paymentSettings) === null || _predicate_paymentSettings === void 0 ? void 0 : _predicate_paymentSettings.netTerms) != null) {
        const netTerms = predicate.paymentSettings.netTerms;
        const multiValues = multiValueGroup(netTerms, texts.netDays);
        units.push({
            key: 'netTerms',
            node: multiValues != null ? /*#__PURE__*/ _jsx(OrValueGroupChip, {
                testId: testIds.NetDays,
                field: "netTerms",
                fields: texts.orValueGroup.fields,
                values: multiValues,
                connectorTone: "or",
                tone: settingsTone,
                texts: texts.orValueGroup.texts,
                bi: {
                    edit: {
                        name: 'drafts_ai_search_chip_net_days_edit'
                    },
                    apply: {
                        name: 'drafts_ai_search_chip_net_days_apply'
                    },
                    remove: {
                        name: 'drafts_ai_search_chip_net_days_remove'
                    }
                },
                onValueChange: (index, value)=>callbacks.onNetTermsChange(replaceMultiValue(netTerms, index, value == null ? undefined : Number(value))),
                onRemove: ()=>callbacks.onNetTermsChange(undefined)
            }) : /*#__PURE__*/ _jsx(AiFilterChip, {
                mode: "numberRange",
                testId: testIds.NetDays,
                fieldLabel: isEmptyPredicate(netTerms) ? '' : texts.netDays.label,
                value: editableValue(netTerms),
                valueLabel: valueLabelFor(netTerms, texts.netDays),
                suffix: texts.netDays.suffix,
                tone: settingsTone,
                editable: !isRangeModeOnly(netTerms),
                validate: (input)=>parseNumbers(input) != null,
                onApply: (input)=>{
                    var _parseNumbers;
                    return callbacks.onNetTermsChange(predicateFromNumbers((_parseNumbers = parseNumbers(input)) !== null && _parseNumbers !== void 0 ? _parseNumbers : [], isExcludeMode(netTerms)));
                },
                onRemove: ()=>callbacks.onNetTermsChange(undefined),
                biPrefix: "drafts_ai_search_chip_net_days",
                texts: {
                    apply: texts.apply,
                    validationError: texts.netDays.validationError
                }
            })
        });
    }
    const renderedSettings = renderedSettingsFields({
        fields: settingsGrammar.fields,
        paymentSettings: predicate.paymentSettings,
        agreementSettings: predicate.agreementSettings
    });
    if (renderedSettings.length > 0) {
        units.push({
            key: 'settings',
            node: /*#__PURE__*/ _jsx(SettingsChips, {
                testId: testIds.Settings,
                grammar: settingsGrammar,
                paymentSettings: predicate.paymentSettings,
                agreementSettings: predicate.agreementSettings,
                onPaymentSettingsChange: callbacks.onPaymentSettingsChange,
                onAgreementSettingsChange: callbacks.onAgreementSettingsChange
            })
        });
    }
    if (showServices && predicate.services != null) {
        units.push({
            key: 'services',
            node: /*#__PURE__*/ _jsx(ServicesChips, {
                testId: testIds.Services,
                grammar: serviceGrammar,
                services: predicate.services,
                onChange: callbacks.onServicesChange
            })
        });
    }
    return units;
};
